import React, { useState, useContext, useEffect, useRef } from "react";
import { Context } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import ApiEventos from "../services/eventoService";
import ApiAlunos from "../services/alunoService";
import ApiAnuidades from "../services/anuidadeService";

import FormaPagamento from "../components/FormaPagamento";
import ValorMonetario from "../components/ValorMonetario";
import BloqueioEvento from "../components/BloqueioEvento";
import InputCPF from "../components/InputCpf";

import useBloqueioEvento from "../hooks/useBloqueioEvento";
import ReCAPTCHA from "react-google-recaptcha";

const CursosSeminariosInscricao = ({ evento }) => {

    const { handleLogin, handleLogout } = useContext(Context);
    const navigate = useNavigate();

    const user = JSON.parse(localStorage.getItem("user"));
    const token = localStorage.getItem("token");
    const ano = new Date().getFullYear();

    const isProcessing = useRef(false);

    const [mostrarCadastro, setMostrarCadastro] = useState(false);
    const [nome, setNome] = useState(user?.name || "");
    const [email, setEmail] = useState(user?.email || "");
    const [cpf, setCpf] = useState("");
    const [cpfValido, setCpfValido] = useState(false);
    const [pedido, setPedido] = useState(null);
    const [anuidadePedido, setAnuidadePedido] = useState(null);
    const [verificandoCpf, setVerificandoCpf] = useState(false);
    const [recaptchaToken, setRecaptchaToken] = useState(null);

    /* =========================
       INIT
    ========================== */

    useEffect(() => {
        if (user?.id) {
            carregarAluno(user.id);
            carregarAnuidade();
        }
    }, []);

    /* =========================
       API
    ========================== */

    const carregarAluno = async (userId) => {
        try {

            const response = await ApiAlunos.getAlunoPorUserId(userId, token);

            if (response.statusRetorno === 401) {
                handleLogout();
                navigate("/");
                return;
            }

            setCpf(response.data.aluno[0].cpf);

        } catch (err) {
            console.error(err);
        }
    };

    const carregarAnuidade = async () => {
        try {

            const response = await ApiAnuidades.listarAnuidadeAluno(ano, token);

            if (response.statusRetorno === 401) {
                handleLogout();
                navigate("/");
                return;
            }

            if (response?.data?.length) {

                const anuidade = response.data[0];

                setAnuidadePedido(
                    anuidade.pedido?.semPedido === 0
                        ? anuidade.pedido
                        : null
                );

            }

        } catch (error) {

            Swal.fire(
                "Erro",
                "Erro ao consultar sua anuidade",
                "error"
            );

        }
    };

    /* =========================
       BLOQUEIO
    ========================== */

    const bloqueio = useBloqueioEvento({
        user,
        evento,
        anuidadePedido,
        ano
    });

    /* =========================
       RECAPTCHA
    ========================== */

    const handleRecaptcha = (token) => {
        setRecaptchaToken(token);
    };

    /* =========================
       CPF
    ========================== */

    const verificarCpfExistente = async (cpfDigitado) => {

        if (!cpfDigitado || cpfDigitado.length < 11) return;

        try {

            setVerificandoCpf(true);

            const response = await ApiAlunos.getAlunoPorCpf(cpfDigitado);

            if (response?.data?.[0]?.user_id) {

                Swal.fire({
                    icon: "warning",
                    title: "CPF já cadastrado",
                    text: "Este CPF já possui cadastro. Faça login para continuar.",
                    confirmButtonText: "Ir para login"
                }).then(() => {

                    setCpf("");
                    setCpfValido(false);
                    setNome("");
                    setEmail("");
                    setMostrarCadastro(false);

                    showLoginPopup();

                });

            }

        } finally {

            setVerificandoCpf(false);

        }
    };

    /* =========================
       LOGIN POPUP
    ========================== */

    const showLoginPopup = () => {

        Swal.fire({
            title: "Acesso para Filiados",
            html: `
                <input type="email" id="swal-email" class="swal2-input" placeholder="Email">
                <input type="password" id="swal-password" class="swal2-input" placeholder="Senha">
            `,
            showCancelButton: true,
            confirmButtonText: "Entrar",

            preConfirm: () => {

                const email = document.getElementById("swal-email").value;
                const password = document.getElementById("swal-password").value;

                if (!email || !password) {
                    Swal.showValidationMessage("Preencha todos os campos");
                }

                return { email, password };

            }

        }).then((result) => {

            if (result.isConfirmed) {

                handleLogin(
                    result.value.email,
                    result.value.password,
                    () => navigate(0)
                );

            }

        });

    };

    /* =========================
       PAGAMENTO
    ========================== */

    const handlePagamento = async () => {

        if (isProcessing.current) return;

        if (!nome || !email || !cpf) {
            Swal.fire("Atenção", "Todos os campos são obrigatórios", "warning");
            return;
        }

        try {

            isProcessing.current = true;

            const response = await ApiEventos.gerarPedidoCursoSeminario({
                nome,
                email,
                cpf,
                eventoId: evento?.id,
                usuarioId: user?.id ?? "0",
                tipoEvento: "CursoSeminario"

            });

            if (response.statusRetorno === 401) {
                handleLogout();
                navigate("/");
                return;
            }

            if (response.statusRetorno === 200) {
                setPedido(response.pedido);
                setMostrarCadastro(false);
            }

        } catch (err) {

            Swal.fire(
                "Erro",
                "Erro ao gerar pedido",
                "error"
            );

        } finally {

            isProcessing.current = false;

        }

    };

    /* =========================
       RENDER
    ========================== */

    return (

        <div className="container">

            <h3 className="mb-3">Participe!</h3>

            {bloqueio && (
                <BloqueioEvento
                    tipo={bloqueio.tipo}
                    mensagem={bloqueio.mensagem}
                    acaoLabel={bloqueio.acaoLabel}
                    acaoRota={bloqueio.acaoRota}
                />
            )}

            {!bloqueio && !user?.id && !mostrarCadastro && (
                <>
                    <button
                        className="btn-main color-2"
                        onClick={showLoginPopup}
                    >
                        Filiados & Vinculados
                    </button>

                    <button
                        className="btn-main color-2"
                        onClick={() => setMostrarCadastro(true)}
                    >
                        Convidados
                    </button>
                </>
            )}

            {!bloqueio && (user?.id || mostrarCadastro) && (

                <form className="form-border">

                    <div className="row">

                        <div className="col-md-6">

                            <label>CPF</label>

                            <InputCPF
                                value={cpf}
                                onChange={setCpf}
                                onBlur={verificarCpfExistente}
                                onValidChange={setCpfValido}
                                required
                            />

                            <label className="mt-2">Nome</label>

                            <input
                                className="form-control"
                                value={nome}
                                onChange={(e) => setNome(e.target.value)}
                            />

                            <label className="mt-2">Email</label>

                            <input
                                className="form-control"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />

                        </div>

                        <div className="col-md-6">

                            <div className="event-info">

                                <h4>{evento?.nome}</h4>

                                <p>
                                    Taxa:
                                    <ValorMonetario
                                        valor={evento.taxaGeral}
                                    />
                                </p>

                                <button
                                    type="button"
                                    className="btn-main color-2"
                                    disabled={verificandoCpf}
                                    onClick={handlePagamento}
                                >
                                    Realizar Pagamento
                                </button>

                                <ReCAPTCHA
                                    sitekey="6LcUbMAqAAAAAOMBtfGI2P4zOORcwfYaKrUbZPuj"
                                    onChange={handleRecaptcha}
                                />

                            </div>

                        </div>

                    </div>

                </form>

            )}

            {pedido && pedido.status !== 1 && (
                <FormaPagamento pedido={pedido} />
            )}

        </div>

    );

};

export default CursosSeminariosInscricao;